// @flow

import React, { Component, Fragment } from "react";
import {
  bindAll,
  findIndex,
  isEqual,
  last,
  reduce,
  get,
  toNumber,
  toString,
  times,
  filter,
  map,
  noop
} from "lodash";
import type {
  Probable,
  ProbableBetCombo,
  RaceTypeCodeEnum
} from "@tvg/types/Race";
import { formatCurrency } from "@tvg/formatter";
import type { TabItem } from "@tvg/types/Common";

import RunnerNumber from "../../_atom/RunnerNumber";
import TabGroup from "../../_molecule/TabGroup";
import MessageBox from "../../_molecule/MessageBox";
import {
  TableHolder,
  Table,
  Tr,
  Th,
  Anchor,
  Td,
  Runner,
  ColorSection,
  PoolAmountText
} from "./styled-components";

type State = {
  selectedtab: number,
  selectedProbable: Probable,
  xRunners: number,
  yRunners: number
};

type Props = {
  probables: Array<Probable> | null,
  /**
   * Race type code
   */
  raceTypeCode: RaceTypeCodeEnum,
  /**
   * WagerType from where we will present probables
   */
  wagerTypeLimit: number,
  /**
   * Function call for gtm event after a tab has been click
   */
  handleGtmOnTabClick: Function
};

export default class RaceProbables extends Component<Props, State> {
  static defaultProps = {
    probables: null,
    raceTypeCode: "T",
    wagerTypeLimit: 70,
    handleGtmOnTabClick: noop
  };

  constructor(props: Props) {
    super(props);

    const firstTab = findIndex(
      this.props.probables,
      (probable: Probable) => probable.wagerType.id > this.props.wagerTypeLimit
    );

    let exactaIndex = -1;
    exactaIndex = findIndex(
      this.props.probables,
      (probable: Probable) => probable.wagerType.code === "EX"
    );

    const lastCombo = last(
      get(
        this.props,
        `probables[${exactaIndex === -1 ? firstTab : exactaIndex}].betCombos`,
        null
      )
    );

    this.state = {
      selectedtab: exactaIndex === -1 ? firstTab : exactaIndex,
      selectedProbable: get(
        this.props,
        `probables[${exactaIndex === -1 ? firstTab : exactaIndex}]`,
        null
      ),
      xRunners: toNumber(get(lastCombo, "runner1", 0)),
      yRunners: toNumber(get(lastCombo, "runner2", 0))
    };

    bindAll(this, [
      "buildTabs",
      "handleTabClick",
      "renderTableHeader",
      "renderProbables",
      "renderRunnerCombos"
    ]);
  }

  componentWillReceiveProps(nextProps: Props) {
    if (this.state.selectedtab < 0) {
      const firstTab = findIndex(
        nextProps.probables,
        (probable: Probable) => probable.wagerType.id > 30
      );

      let exactaIndex = -1;
      exactaIndex = findIndex(
        nextProps.probables,
        (probable: Probable) => probable.wagerType.code === "EX"
      );

      const lastCombo = last(
        get(
          nextProps,
          `probables[${exactaIndex === -1 ? firstTab : exactaIndex}].betCombos`,
          null
        )
      );

      this.setState({
        selectedtab: exactaIndex === -1 ? firstTab : exactaIndex,
        selectedProbable: get(
          nextProps,
          `probables[${exactaIndex === -1 ? firstTab : exactaIndex}]`,
          null
        ),
        xRunners: toNumber(get(lastCombo, "runner1", 0)),
        yRunners: toNumber(get(lastCombo, "runner2", 0))
      });
    } else {
      const lastCombo = last(
        get(nextProps, `probables[${this.state.selectedtab}].betCombos`, null)
      );

      this.setState({
        selectedProbable: get(
          nextProps,
          `probables[${this.state.selectedtab}]`,
          null
        ),
        xRunners: toNumber(get(lastCombo, "runner1", 0)),
        yRunners: toNumber(get(lastCombo, "runner2", 0))
      });
    }
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    return (
      get(nextProps, "probables.length", 0) !==
        get(this.props, "probables.length", 0) ||
      !isEqual(nextProps, this.props) ||
      !isEqual(nextState, this.state)
    );
  }

  handleTabClick(newTabIndex: number, section: string) {
    this.props.handleGtmOnTabClick(section);
    if (this.state.selectedtab !== newTabIndex) {
      const lastCombo = last(
        get(this.props, `probables[${newTabIndex}].betCombos`, null)
      );

      this.setState({
        selectedtab: newTabIndex,
        selectedProbable: get(this.props, `probables[${newTabIndex}]`, null),
        xRunners: toNumber(get(lastCombo, "runner1", 0)),
        yRunners: toNumber(get(lastCombo, "runner2", 0))
      });
    }
  }

  buildTabs() {
    return reduce(
      get(this.props, "probables", []),
      (tabs: Array<TabItem>, probable: Probable, index: number) => {
        if (probable.wagerType.id > 30) {
          const minWagerAmountString = formatCurrency(
            probable.minWagerAmount,
            "USD",
            probable.minWagerAmount.toString().length > 1 ? 2 : 0
          );

          tabs.push({
            title: `${minWagerAmountString} ${probable.wagerType.name}`,
            subTitle: (
              <PoolAmountText>
                {formatCurrency(probable.amount, "USD", 0)}
              </PoolAmountText>
            ),
            isActive: this.state.selectedtab === index,
            onClick: () => {
              this.handleTabClick(
                index,
                `${minWagerAmountString} ${probable.wagerType.name}`
              );
            }
          });
        }
        return tabs;
      },
      []
    );
  }

  renderTableHeader() {
    return (
      this.state.xRunners > 0 && (
        <thead>
          <Tr>
            <Th>
              <Anchor>
                <span>
                  1<sup>st</sup>
                </span>
                <span>
                  2<sup>nd</sup>
                </span>
              </Anchor>
            </Th>
            {times(this.state.xRunners, (n: number) => (
              <Td key={`probable-column-runner-${n}`}>
                <Runner>
                  <RunnerNumber
                    runnerId={`${n + 1}`}
                    raceTypeCode={this.props.raceTypeCode}
                  />
                </Runner>
              </Td>
            ))}
          </Tr>
        </thead>
      )
    );
  }

  renderProbables() {
    return (
      this.state.yRunners > 0 && (
        <tbody>
          {times(this.state.yRunners, (n: number) =>
            this.renderRunnerCombos(`${n + 1}`)
          )}
        </tbody>
      )
    );
  }

  renderRunnerCombos(runnerId: string) {
    const combos = filter(
      this.state.selectedProbable.betCombos,
      (combo: ProbableBetCombo) => combo.runner2 === runnerId
    );

    return (
      <Tr key={`probable-line-runner-${runnerId}`}>
        <Th>
          <Runner>
            <RunnerNumber
              runnerId={runnerId}
              raceTypeCode={this.props.raceTypeCode}
            />
          </Runner>
        </Th>

        {map(combos, (combo: ProbableBetCombo) => {
          const payout = toNumber(combo.payout);

          return (
            <Td
              key={`probable-combo-${combo.runner1}x${toString(combo.runner2)}`}
            >
              {payout > 0 ? formatCurrency(Math.floor(payout), "USD", 0) : "-"}
            </Td>
          );
        })}
      </Tr>
    );
  }

  render() {
    if (this.props.probables) {
      return (
        <ColorSection>
          <div style={{ marginBottom: "1px" }}>
            <TabGroup tabs={this.buildTabs()} stretch={false} />
          </div>
          <TableHolder>
            <Table>
              {this.renderTableHeader()}
              {this.renderProbables()}
            </Table>
          </TableHolder>
        </ColorSection>
      );
    }

    return (
      <MessageBox
        type="info"
        message={
          <Fragment>
            Probables information isn't available yet
            <br />
            Please check back later
          </Fragment>
        }
        title="Probables Unavailable"
        isVertical
      />
    );
  }
}
